import is from '@sindresorhus/is';
import { createContext, type FunctionComponent, type PropsWithChildren, type ReactNode, useContext } from 'react';

type ChatBodyOptions = {
    readonly aboveComposer?: ReactNode;
    readonly closable?: boolean;
    readonly columnClassName?: string;
    readonly composerPlaceholder?: string;
    readonly frameClassName?: string;
    /** Replaces the message list while the conversation is empty. */
    readonly greeting?: ReactNode;
    readonly headerActions?: ReactNode;
    /** Shows the greeting and the plain composer in place of a conversation that has messages. */
    readonly hidesConversation?: boolean;
    readonly showsStarterPrompts?: boolean;
    /** Off when the page renders `ChatThreadsRailSlot` beside more than the chat. */
    readonly showsThreadsRail?: boolean;
    readonly showsTitle?: boolean;
    readonly top?: ReactNode;
};

export type ChatBodyProps = ChatBodyOptions & {
    /** The wide toggle resizes the right dock; only dock mounts opt in. */
    readonly showWideToggle?: boolean;
};

export type UserTextPresence = 'absent' | 'present' | 'unknown';

export type ConversationEmptinessOptions = {
    /** Whether the answer is needed at all; a disabled read never loads the thread. */
    readonly isEnabled: boolean;
};

/** One way of talking to the agent: the legacy chat over SSE envelopes, or the AG-UI chat. */
export type ChatImplementation = {
    /** Runs the conversation and provides it to the composer, the header and the body. */
    readonly ConversationProvider: FunctionComponent<PropsWithChildren>;
    /** The chat card with the active thread, inside the conversation provider. */
    readonly Body: FunctionComponent<ChatBodyProps>;
    /** What keeps running inside the composer while the dock is closed, such as redirects to changed items. */
    readonly ComposerEffects?: FunctionComponent;
    /**
     * Whether the conversation on screen is known to hold no message, not even a request still on
     * its way. Fixed per provider, like the implementation itself, so it is called like any hook.
     */
    readonly useIsConversationEmpty: (options: ConversationEmptinessOptions) => boolean;
    /**
     * Whether the conversation on screen holds a plain user message with this text, sent or still on
     * its way; `unknown` while its history is still loading.
     */
    readonly useUserTextPresence: (text: string | undefined) => UserTextPresence;
};

export const ChatImplementationContext = createContext<ChatImplementation | null>(null);

export const useChatImplementation = (): ChatImplementation => {
    const value = useContext(ChatImplementationContext);
    if (is.null(value)) {
        throw new Error('useChatImplementation must be used within a ChatComposerProvider');
    }
    return value;
};

/** Whether the conversation on screen holds a plain user message with this text, whichever chat runs it. */
export const useUserTextPresence = (text: string | undefined): UserTextPresence =>
    useChatImplementation().useUserTextPresence(text);
